<template>
  <div v-if="title" class="node-pension-fund">
    <PageHeader
      :lead="
        $texts(
          'employerPageLead',
          'Do you have a question? Contact us without hesitation.',
        )
      "
      :tagline="
        $texts(
          'employerPageTagline',
          'Ihr Arbeitgeber – Ihre Ansprechperson bei PUBLICA',
        )
      "
      :title="title"
    />

    <nav v-if="groups.length > 1" class="" aria-label="Go to group">
      <div class="container">
        <ul class="flex flex-wrap gap-[10px]">
          <li v-for="group in groups" :key="group.label">
            <a
              :href="`#section-${group.group}`"
              class="inline-flex items-center gap-8 text-ellipsis text-nowrap rounded-full bg-blue-100 px-24 py-12 text-lg text-blue-700 hover:bg-white focus:bg-white focus:outline-1 focus:outline-offset-2 focus:outline-blue-500"
            >
              {{ group.label }}
              <SpriteSymbol name="arrow-down" class="size-24" />
            </a>
          </li>
        </ul>
      </div>
    </nav>

    <section
      v-for="group in groups"
      :id="`section-${group.group}`"
      :key="group.label"
      class="section"
    >
      <div class="is-wide container">
        <SectionTitle :title="group.label" class="has-large-margin" />

        <div class="grid gap-24 md:gap-32 lg:gap-40 xl:gap-56">
          <OverlayedTeaser
            v-for="item in group.responsibilities"
            :key="item.id"
            :media="item.person.image"
          >
            <PersonCard
              v-bind="item.person"
              :tagline="item.tagline"
              class="card"
            />
          </OverlayedTeaser>
        </div>
      </div>
    </section>

    <section v-if="pensionFundOverviewTitle" class="section">
      <div class="is-wide container">
        <SectionTitle
          :title="pensionFundOverviewTitle"
          class="has-large-margin"
        />
        <div class="teaser-grid">
          <div v-for="(link, i) in pensionFundLinks" :key="'teaser_' + i">
            <Teaser
              :to="link.to"
              :title="link.title"
              :body="link.description"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'
import type { LinkItem } from '~/types/global'
import type {
  EmployerResponsibilityFragment,
  NodeEmployerFragment,
  NodeEmployerPensionFundFragment,
  PersonCardFragment,
} from '#graphql-operations'
import { notEmpty, onlyUnique } from '~/helpers'

const { $texts } = useNuxtApp()
const initData = await useInitData()

const props = defineProps<{
  __typename?: string
  title?: string
  intro?: string
  subtitle?: string
  referencedResponsibilities?: NodeEmployerFragment['referencedResponsibilities']
  referencedPensionFund?: NodeEmployerFragment['referencedPensionFund']
}>()

type TargetGroup = 'active' | 'passive' | 'iv'

type Responsibility = {
  id: string
  person: PersonCardFragment
  group: TargetGroup
  groupLabel: string
  tagline: string
  namesStart: string
}

type ResponsibilityGroup = {
  label: string
  group: TargetGroup
  responsibilities: Responsibility[]
}

const pensionFund = computed((): NodeEmployerPensionFundFragment | null => {
  const entity = props.referencedPensionFund
  return entity && entity.__typename === 'NodePensionFund' ? entity : null
})

const isClosedPensionFund = computed((): boolean => {
  const nid = pensionFund.value?.nid?.toString()
  return nid === initData.value.siteConfig.closedPensionFund?.id
})

const responsibilities = computed((): Responsibility[] => {
  const entities = props.referencedResponsibilities?.query.items || []
  const items = entities
    .map((v) => {
      return v && 'id' in v ? v : null
    })
    .filter(notNullish)
  return items
    .map((v) => {
      if (v.referencedPerson?.__typename === 'StoragePerson') {
        const groupLabel = getTargetGroupLabel(v.targetGroup)
        return {
          id: v.id!,
          tagline: getPersonTagline(v),
          person: v.referencedPerson,
          groupLabel,
          namesStart: v.targetNamesFrom || '',
          group: v.targetGroup as Responsibility['group'],
        }
      }
      return null
    })
    .filter(notEmpty)
})

const GROUP_SORT: TargetGroup[] = ['active', 'passive', 'iv']

const groups = computed((): ResponsibilityGroup[] => {
  // Check case if the same person is responsible for all target groups.
  // In this case only one group is output with a combined title indicating
  // both groups.
  if (responsibilities.value.length === 2) {
    const isSamePerson =
      responsibilities.value.map((v) => v.person.id).filter(onlyUnique)
        .length === 1
    if (isSamePerson) {
      const responsibility = responsibilities.value[0]!
      return [
        {
          label: $texts(
            'targetGroupBoth',
            'For insured persons and pension recipients',
          ),
          responsibilities: [
            {
              ...responsibility,
              tagline: $texts(
                'targetGroupBoth',
                'For insured persons and pension recipients',
              ),
            },
          ],
          group: responsibility.group,
        },
      ]
    }
  }
  const map = responsibilities.value.reduce<
    Record<string, ResponsibilityGroup>
  >((acc, v) => {
    const key = v.groupLabel
    const bucket =
      acc[key] ??
      (acc[key] = {
        label: v.groupLabel,
        responsibilities: [],
        group: v.group,
      })
    bucket.responsibilities.push(v)
    return acc
  }, {})

  return Object.values(map)
    .map((v) => {
      return {
        label: v.label,
        responsibilities: v.responsibilities.sort((a, b) => {
          const textA = a.namesStart
          const textB = b.namesStart
          return textA < textB ? -1 : textA > textB ? 1 : 0
        }),
        group: v.group,
      }
    })
    .sort((a, b) => {
      return GROUP_SORT.indexOf(a.group) - GROUP_SORT.indexOf(b.group)
    })
})

const pensionFundLinks = computed((): LinkItem[] => {
  return (pensionFund.value?.paragraphs || [])
    .map((v) => {
      if (v && 'uuid' in v && v.navSectionTeaser?.title && v.uuid) {
        const id = v.navSectionTeaser?.anchor || v.uuid
        return {
          title: v.navSectionTeaser.title || '',
          to: pensionFund.value?.url?.path + '#' + id,
          description: v.navSectionTeaser.text,
        }
      }

      return null
    })
    .filter(notNullish)
})

const pensionFundOverviewTitle = computed((): string => {
  if (isClosedPensionFund.value) {
    return $texts(
      'pension_fund.closedPensionFundOverview',
      'Closed pension fund overview',
    )
  }
  if (pensionFund.value?.title) {
    return $texts(
      'pension_fund.tilesOverviewTitle',
      'Overview of @pension_plan',
    ).replace('@pension_plan', pensionFund.value.title)
  }
  return ''
})

function getPersonTagline(v: EmployerResponsibilityFragment): string {
  const groupLabel = getTargetGroupLabel(v.targetGroup)
  if (v.targetNamesFrom === 'A' && v.targetNamesTo === 'Z') {
    return groupLabel
  }
  if (v.targetNamesFrom === v.targetNamesTo) {
    return `${groupLabel} ${v.targetNamesFrom}`
  }

  return `${groupLabel} ${v.targetNamesFrom} – ${v.targetNamesTo}`
}

function getTargetGroupLabel(targetGroup = ''): string {
  return targetGroup === 'active'
    ? $texts('targetGroupActive', 'Insured persons')
    : $texts('targetGroupPassive', 'Pension recipients')
}
</script>
